/** @jsxImportSource @emotion/react */
import { LAYER_STAGE, UNIT_OF_ACCOUNT } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { DeviceResultActions } from "../../../../actions/Tenants/config/constants";
import { isOfflineChecksAllowedForTenant } from "../../../../offline/allowedTenants";
import MetaForm from "../../../Forms/MetaForm";
import { CTA_FORM_LOCATION } from "../../AddItem/components/Summary/LayerCtaCheckSummary";
import CheckSummaryCategoryChartsConfigForm from "./CheckSummaryCategoryChartsConfigForm";
import CheckSummaryMinMaxBreakdownConfigForm from "./CheckSummaryMinMaxBreakdownConfigForm";
import DefectGroupConfigForm from "./DefectGroupConfigForm";
import { DisplayFilterForm } from "./DisplayFilterForm";
import FieldConfigForm from "./FieldConfigForm";
import FlowConfigForm from "./FlowConfigForm";
import FormConfigForm from "./FormConfigForm";
import LegacyCheckSummaryFruitTableConfigForm from "./LegacyCheckSummaryFruitTableConfigForm";
import LegacyInlineFormConfigForm from "./LegacyInlineFormConfigForm";
import OptionsConfigForm from "./OptionsConfigForm";
import PressureCalibrationSettingsConfigForm from "./PressureCalibrationSettingsConfigForm";
import RequiredImageConfigForm from "./RequiredImageConfigForm";
import SanitizeUnexpectedFields from "./SanitizeUnexpectedFields";
import { yesOrNoRadio } from "./util";

export const defaultCheckLocation = {
    text: "Your check location",
    value: "your_check_location",
    display_filter: {
        fruit_types: [],
        exclude_fruit_types: [],
        layer_types: [],
        // exclude_layer_types: [],
    },
};

export default function CheckLocationConfigForm({ config_data, setConfigData }) {
    const config = useConfig();
    // Hide the "Offline-enabled" toggle on check-type configs for tenants
    // not on the offline-checks allowlist (Bakker + playwright-test).
    // See offline/allowedTenants.ts.
    const showOfflineEnabledField = isOfflineChecksAllowedForTenant(config.tenant_id);

    const form = [
        {
            type: "header",
            label: "General configuration",
        },
        {
            label: "Value",
            description: "What is the slug or identifier of this check location. Used in the database and in urls.",
            name: "value",
            type: "text",
            onchange_callback: ({ field, value }) => setConfigData({ ...config_data, [field]: value.trim().replaceAll(" ", "_").toLowerCase() })
        },
        {
            label: "Label",
            description: "What is the label of this check location. Used in the button.",
            name: "text",
            type: "text"
        },
        {
            label: "Start check on overview tabs",
            description: "On which overview tabs should this check be startable? Uses the tab value (stage).",
            name: "start_on_overview_tabs",
            type: "single-select",
            multi: true,
            return_array: true,
            options: config.stages.map(({ value, text }) => ({ value, label: text }))
        },
        {
            label: "Start check on ripening room",
            description: "Show this check as a bulk start button in the ripening room. When enabled, users can select multiple pallets and start this check on them.",
            name: "start_on_ripening_room",
            ...yesOrNoRadio
        },
        {
            label: "Requires an active ripening cycle",
            description: "When enabled, this check can only be started in the ripening room if there is an active ripening cycle.",
            name: "requires_active_ripening_cycle",
            ...yesOrNoRadio
        },
        {
            label: "Unit of account",
            description: "You can use this to overwrite the unit of account of the fruit type. Use this or the fruit type config to set the unit of account for a box weight check for example. When in doubt, leave this empty.",
            name: "unit_of_account",
            type: "single-select",
            options: Object.keys(UNIT_OF_ACCOUNT).map((i) => ({ value: UNIT_OF_ACCOUNT[i], label: i }))
        },
        {
            label: "Allocate to stage",
            description: "If set, creating a check of this type allocates the layer to this stage (via a virtual location for that stage).",
            name: "allocate_to_stage",
            type: "single-select",
            options: Object.keys(LAYER_STAGE).map((i) => ({ value: LAYER_STAGE[i], label: i }))
        },
        {
            label: "Allocate parent of type",
            description: "If set (and Allocate to stage is set), allocating a pallet will also allocate its linked parent of this type to the same stage. Siblings will also be allocated to the same stage.",
            name: "allocate_parent_of_type",
            type: "single-select",
            multi: true,
            return_array: true,
            options: config.layer_types.map((i) => ({ value: i.value, label: i.text }))
        },
        {
            type: "header",
            label: "Retrospective grouping",
        },
        {
            label: "Retrospective grouping parent types",
            description: "Only layers sharing a direct parent of one of these types can be grouped with a completed check.",
            name: "retrospective_group_parent_types",
            type: "single-select",
            multi: true,
            return_array: true,
            options: config.layer_types.map((i) => ({ value: i.value, label: i.text }))
        },
        {
            type: "element",
            name: "retrospective_group_by_fields",
            label: "Retrospective grouping fields",
            description: "Layer meta attributes that must match before this completed check can be attached to layers sharing its direct parent. Use display filters to choose attributes per fruit or layer type.",
            el: <OptionsConfigForm
                fields={config_data.retrospective_group_by_fields || []}
                description="Layer meta attributes used to find matching sibling layers."
                name="retrospective_group_by_fields"
                useDisplayFilter={["fruit_types", "exclude_fruit_types", "layer_types"] as any}
                title="Retrospective grouping fields"
                setArray={(retrospective_group_by_fields) => setConfigData({ ...config_data, retrospective_group_by_fields })} />
        },
        {
            label: "Display filter",
            type: "element",
            name: "display_filter",
            el: <DisplayFilterForm
                object={config_data.display_filter}
                setValue={(field, value) => setConfigData({ ...config_data, display_filter: { ...config_data.display_filter, [field]: value } })}
                attributes={["fruit_types", "exclude_fruit_types", "layer_types", "facilities", "exclude_facilities", "meta_conditions"]} />
        },
        // Only allowlisted tenants (Bakker + playwright-test) see the
        // offline-enabled toggle on check types. See allowedTenants.ts.
        ...(showOfflineEnabledField ? [{
            label: "Offline-enabled",
            name: "offline_enabled",
            description: "Make this check type available offline (in addition to online). Defaults to off — check types without this flag are online-only and never appear in the offline check-type selector. Set to Yes for manual-entry check types that should work offline. Mini- and FTA-using check types must stay off (they require live device telemetry that has no offline equivalent).",
            ...yesOrNoRadio,
        }] : []),
        {
            type: "header",
            label: "RipeWise",
        },
        {
            label: "Is this Check linked to a Location / Ripening Room",
            description: "Set this to true if this check is / should be linked to a location / ripening room. This will make sure a check is linked to latest known location when started from layer details page.",
            name: "is_linked_to_latest_location",
            ...yesOrNoRadio
        },
        {
            type: "header",
            label: "Flows and forms",
        },
        {
            label: "Flow",
            description: "Which steps to follow to complete this check",
            name: "flow",
            type: "element",
            el: <FlowConfigForm
                fields={config_data.flow || []}
                description="Which steps to follow to complete this check"
                name="flow"
                title="Flow"
                setArray={(flow) => setConfigData({ ...config_data, flow })} />
        },
        // TODO: delete this field
        {
            label: "Inline form",
            name: "inline_form",
            type: "element",
            el: <LegacyInlineFormConfigForm
                fields={config_data.inline_form || []}
                description="This is the legacy way of defining which fields to show in the form. Use `Fruit Form` if you want to use mandatory fields."
                name="inline_form"
                title="Legacy Form"
                setArray={(inline_form) => setConfigData({ ...config_data, inline_form })} />
        },
        {
            label: "Fruit form",
            description: "Define the form for fruit. This data is stored on a fruit level.",
            name: "fruit_form",
            type: "element",
            el: <FormConfigForm
                fields={config_data.fruit_form || []}
                description="Define the form for fruit. This data is stored on a fruit level."
                name="fruit_form"
                title="Fruit"
                setArray={(fruit_form) => setConfigData({ ...config_data, fruit_form })} />
        },
        {
            label: "Secondary fruit form",
            description: "Define a secondary form for fruit. This data is stored on a fruit level.",
            name: "secondary_fruit_form",
            type: "element",
            el: <FormConfigForm
                fields={config_data.secondary_fruit_form || []}
                description="Define a secondary form for fruit. This data is stored on a fruit level."
                name="secondary_fruit_form"
                title="Secondary fruit"
                setArray={(secondary_fruit_form) => setConfigData({ ...config_data, secondary_fruit_form })} />
        },
        {
            label: "Meta form summary",
            description: "A form to show in the summary of the check. Mostly used for next_action.",
            name: "meta_form_summary",
            type: "element",
            el: <FormConfigForm
                fields={config_data.meta_form_summary || []}
                description="A form to show in the summary of the check. Mostly used for next_action."
                name="meta_form_summary"
                title="Meta form summary"
                setArray={(meta_form_summary) => setConfigData({ ...config_data, meta_form_summary })} />
        },
        {
            label: "Meta form layer",
            description: "A form to show before you start this check. This data is stored on a layer level.",
            name: "meta_form_layer",
            type: "element",
            el: <FormConfigForm
                fields={config_data.meta_form_layer || []}
                description="A form to show before you start this check. This data is stored on a layer level."
                name="meta_form_layer"
                title="Meta form layer"
                setArray={(meta_form_layer) => setConfigData({ ...config_data, meta_form_layer })} />
        },
        {
            label: "Pre check form",
            description: "A form to show before you start this check. This data is stored on a check level.",
            name: "pre_check_form",
            type: "element",
            el: <FormConfigForm
                fields={config_data.pre_check_form || []}
                description="A form to show before you start this check. This data is stored on a check level."
                name="pre_check_form"
                title="Pre check form"
                setArray={(pre_check_form) => setConfigData({ ...config_data, pre_check_form })} />
        },
        {
            label: "Required check images",
            description: "If you want images to be taken on layer level. Configure the type of images here. Used on check summary and pdf.",
            type: "element",
            name: "required_images",
            el: <RequiredImageConfigForm
                fields={(config_data.required_images || []).map((i) => ({ ...i, label: typeof i.label === "object" ? i.type : i.label })) || []} // Whoops...used <br</br> as label in hardcoded config and forget to sanitze it to a string
                description="If you want images to be taken on all the fruit or the whole pallet. Configure the type of images here. Used on check summary, advance ripening and visual check."
                name="required_images"
                title="Required images"
                setArray={(required_images) => setConfigData({ ...config_data, required_images })} />
        },
        {
            type: "header",
            label: "Visual check settings"
        },
        {
            label: "Visual check form",
            description: "A form to show at the visual check. e.g. sample_size",
            name: "visual_check_form",
            type: "element",
            el: <FormConfigForm
                fields={config_data.visual_check_form || []}
                description="A form to show at the visual check. e.g. sample_size"
                name="visual_check_form"
                title="Visual check form"
                setArray={(visual_check_form) => setConfigData({ ...config_data, visual_check_form })} />
        },
        {
            label: "Visual check defect form",
            description: "A form to enter defect frequencies at the visual check.",
            name: "visual_check_defect_form",
            type: "element",
            el: <DefectGroupConfigForm
                fields={config_data.visual_check_defect_form || []}
                description="A form to enter defect frequencies at the visual check."
                name="visual_check_defect_form"
                title="Visual check defect form"
                setArray={(visual_check_defect_form) => setConfigData({ ...config_data, visual_check_defect_form })} />
        },
        {
            type: "header",
            label: "Secondary visual check settings"
        },
        {
            label: "Secondary visual check form",
            description: "A form to show at the secondary visual check. e.g. sample_size",
            name: "secondary_visual_check_form",
            type: "element",
            el: <FormConfigForm
                fields={config_data.secondary_visual_check_form || []}
                description="A form to show at the secondary visual check. e.g. sample_size"
                name="secondary_visual_check_form"
                title="Secondary visual check form"
                setArray={(secondary_visual_check_form) => setConfigData({ ...config_data, secondary_visual_check_form })} />
        },
        {
            label: "Secondary visual check defect form",
            description: "A form to enter defect frequencies at the secondary visual check.",
            name: "secondary_visual_check_defect_form",
            type: "element",
            el: <DefectGroupConfigForm
                fields={config_data.secondary_visual_check_defect_form || []}
                description="A form to enter defect frequencies at the secondary visual check."
                name="secondary_visual_check_defect_form"
                title="Secondary visual check defect form"
                setArray={(secondary_visual_check_defect_form) => setConfigData({ ...config_data, secondary_visual_check_defect_form })} />
        },

        {
            type: "header",
            label: "Advance ripening settings"
        },
        {
            label: "Advance ripening start form",
            description: "A form to show before starting advance ripening. You will probably want to set the sample size here.",
            name: "advance_ripening_start_form",
            type: "element",
            el: <FormConfigForm
                fields={config_data.advance_ripening_start_form || []}
                description="A form to show before starting advance ripening. You will probably want to set the sample size here."
                name="advance_ripening_start_form"
                title="Advance ripening start form"
                setArray={(advance_ripening_start_form) => setConfigData({ ...config_data, advance_ripening_start_form })} />
        },
        {
            label: "Advance ripening (firmness) classes form",
            description: "Which classes to collect at advance ripening (other than defects). The sum of these defines sample_size.",
            name: "advance_ripening_classes_form",
            type: "element",
            el: <FormConfigForm
                fields={config_data.advance_ripening_classes_form || []}
                description="Which classes to collect at advance ripening (other than defects). The sum of these defines sample_size."
                name="advance_ripening_classes_form"
                title="Advance ripening (firmness) classes form"
                setArray={(advance_ripening_classes_form) => setConfigData({ ...config_data, advance_ripening_classes_form })} />
        },
        {
            label: "Advance ripening form",
            description: "A form to show at the end of advance ripening. For example a next action.",
            name: "meta_form_advance_ripening",
            type: "element",
            el: <FormConfigForm
                fields={config_data.meta_form_advance_ripening || []}
                description="A form to show at the end of advance ripening. For example a next action."
                name="meta_form_advance_ripening"
                title="Advance ripening form"
                setArray={(meta_form_advance_ripening) => setConfigData({ ...config_data, meta_form_advance_ripening })} />
        },
        {
            label: "Show visual check in advance ripening",
            name: "advance_ripening_show_external_defects",
            description: "Show visual check in the advance ripening form and check card. Configure visual_check_defect_form to define which defects to show.",
            ...yesOrNoRadio
        },
        {
            type: "header",
            label: "Report & summary options",
        },
        {
            label: "Summary layer fields PDF",
            description: "What layer meta to show in pdf above the fruit table in the pdf check summary. (Check Summary Pdf)",
            type: "element",
            name: "summary_layer_fields_pdf",
            el: (
                <FieldConfigForm
                    fields={config_data.summary_layer_fields_pdf || []}
                    description="What layer meta to show in pdf above the fruit table in the pdf check summary. (Check Summary Pdf)"
                    name="summary_layer_fields_pdf"
                    title="Summary layer fields PDF"
                    setArray={(summary_layer_fields_pdf) => setConfigData({ ...config_data, summary_layer_fields_pdf })}
                />
            )
        },
        {
            label: "Summary layer fields col 1",
            description: "What layer meta to show in column 1 above the fruit table in the check summary.",
            type: "element",
            name: "summary_layer_fields_1",
            el: (
                <FieldConfigForm
                    fields={config_data.summary_layer_fields_1 || []}
                    description="What layer meta to show in column 1 above the fruit table in the check summary."
                    name="summary_layer_fields_1"
                    title="Summary layer fields col 1"
                    setArray={(summary_layer_fields_1) => setConfigData({ ...config_data, summary_layer_fields_1 })}
                />
            )
        },
        {
            label: "Summary layer fields col 2",
            description: "What layer meta to show in column 2 above the fruit table in the check summary.",
            type: "element",
            name: "summary_layer_fields_2",
            el: (
                <FieldConfigForm
                    fields={config_data.summary_layer_fields_2 || []}
                    description="What layer meta to show in column 2 above the fruit table in the check summary."
                    name="summary_layer_fields_2"
                    title="Summary layer fields col 2"
                    setArray={(summary_layer_fields_2) => setConfigData({ ...config_data, summary_layer_fields_2 })}
                />
            )
        },
        {
            label: "Summary layer fields col 3",
            description: "What layer meta to show in column 3 above the fruit table in the check summary.",
            type: "element",
            name: "summary_layer_fields_3",
            el: (
                <FieldConfigForm
                    fields={config_data.summary_layer_fields_3 || []}
                    description="What layer meta to show in column 3 above the fruit table in the check summary."
                    name="summary_layer_fields_3"
                    title="Summary layer fields col 3"
                    setArray={(summary_layer_fields_3) => setConfigData({ ...config_data, summary_layer_fields_3 })}
                />
            )
        },
        {
            label: "Summary layer fields col 4",
            description: "What layer meta to show in column 4 above the fruit table in the check summary.",
            type: "element",
            name: "summary_layer_fields_4",
            el: (
                <FieldConfigForm
                    fields={config_data.summary_layer_fields_4 || []}
                    description="What layer meta to show in column 4 above the fruit table in the check summary."
                    name="summary_layer_fields_4"
                    title="Summary layer fields col 4"
                    setArray={(summary_layer_fields_4) => setConfigData({ ...config_data, summary_layer_fields_4 })}
                />
            )
        },
        {
            label: "Min Max value breakdown",
            description: "For which fields you want to show the min, max and average class in the summary.",
            type: "element",
            name: "summary_min_max_breakdown",
            el: <CheckSummaryMinMaxBreakdownConfigForm
                fields={config_data.summary_min_max_breakdown || []}
                description="For which fields you want to show the min, max and average class in the summary. "
                name="summary_min_max_breakdown"
                title="Min Max value breakdown"
                setArray={(summary_min_max_breakdown) => setConfigData({ ...config_data, summary_min_max_breakdown })} />
        },
        {
            label: "Category chart",
            description: "For which fields do you want to show a pie/bar chart in the summary.",
            type: "element",
            name: "summary_category_charts",
            el: <CheckSummaryCategoryChartsConfigForm
                fields={config_data.summary_category_charts || []}
                description="For which fields do you want to show a pie/bar chart in the summary. "
                name="summary_category_charts"
                title="Category chart"
                setArray={(summary_category_charts) => setConfigData({ ...config_data, summary_category_charts })} />
        },
        {
            label: "Defect groups",
            description: "What group of defects do you want to show at the header of summary and pdf page?",
            name: "defect_groups",
            type: "element",
            el: <DefectGroupConfigForm
                fields={config_data.defect_groups || []}
                description="What group of defects do you want to show at the header of summary and pdf page?"
                name="defect_groups"
                title="Defect groups"
                setArray={(defect_groups) => setConfigData({ ...config_data, defect_groups })} />
        },
        {
            label: "Legacy summary fruit fields",
            description: "Fields to show in the fruit table in the check summary",
            type: "element",
            name: "summary_fruit_fields",
            el: <LegacyCheckSummaryFruitTableConfigForm
                fields={config_data.summary_fruit_fields || []}
                description="Fields to show in the fruit table in the check summary"
                name="summary_fruit_fields"
                title="Legacy summary fruit fields" setArray={(summary_fruit_fields) => setConfigData({ ...config_data, summary_fruit_fields })} />
        },
        {
            label: "Summary fruit fields",
            description: "Fields to show in the fruit table in the check summary",
            type: "element",
            name: "summary_fruit_fields_v2",
            el: (
                <FieldConfigForm
                    fields={config_data.summary_fruit_fields_v2 || []}
                    description="Fields to show in the fruit table in the check summary"
                    name="summary_fruit_fields_v2"
                    title="Summary fruit fields"
                    setArray={(summary_fruit_fields_v2) => setConfigData({ ...config_data, summary_fruit_fields_v2 })}
                />
            )
        },
        {
            type: "header",
            label: "Pressure calibration",
        },
        {
            label: "Pressure calibration settings",
            description: "Settings for pressure calibration",
            type: "element",
            name: "pressure_calibration_settings",
            el: <PressureCalibrationSettingsConfigForm
                fields={config_data.pressure_calibration_settings || []}
                description="Settings for pressure calibration"
                name="pressure_calibration_settings"
                title="Pressure calibration settings"
                setArray={(pressure_calibration_settings) => setConfigData({ ...config_data, pressure_calibration_settings })} />
        },
        {
            label: "Device result action",
            description: "If we are using a device, what should happen right after we received fruit results from the device?",
            name: "device_result_action",
            type: "single-select",
            options: Object.keys(DeviceResultActions).map((i) => ({ value: i, label: DeviceResultActions[i] }))
        },
        {
            type: "header",
            label: "QC Planning or other CTA form for layer",
        },
        {
            label: "Call to action title",
            name: "cta_form_title",
            description: "Title of the call to action form.",
            type: "text"
        },
        {
            label: "Where to show the CTA form",
            name: "cta_form_location",
            type: "single-select",
            options: [
                { value: CTA_FORM_LOCATION.MODAL_AFTER_SUBMIT, label: "Modal after submit" },
                { value: CTA_FORM_LOCATION.BELOW_CHECK_SUMMARY, label: "Below Check summary (inline)" },
            ]
        },
        {
            type: "element",
            label: "Filter",
            description: "Call to action form for the layer at check summary.",
            el: <FormConfigForm
                fields={config_data.cta_form || []}
                description="Call to action form for the layer at check summary."
                name="cta_form"
                title="Filter"
                setArray={(cta_form) => setConfigData({ ...config_data, cta_form })} />
        },
        {
            type: "header",
            label: "Notifications",
        },
        {
            label: "Enable send notification when check is done",
            description: "Enable the option to send a notification when the check is done. The recipients should be configured in internal emails.",
            name: "enable_send_notification",
            ...yesOrNoRadio
        },
        {
            label: "Send status to external service",
            description: "When a check is created at this location, automatically send the check status to the tenant's configured external service (e.g. SAP/ERP). Requires backend service mapping for the tenant.",
            name: "send_status_to_external_service",
            ...yesOrNoRadio
        },
        {
            label: "Notification recipients",
            description: "Email addresses to send the notification to. Separate multiple emails with a comma.",
            name: "check_email_recipients",
            type: "text",
        },
        {
            label: "Custom email body",
            description: "Custom email body. You can use variables like {{arrival_date_intake}} (field must be configured in check form).",
            name: "check_email_body",
            type: "textarea",
            textInputProps: { rows: 3 }
        },
        {
            label: "Custom email signature",
            description: "Custom email signature.",
            name: "check_email_signature",
            type: "textarea",
            textInputProps: { rows: 3 }
        },
        {
            type: "header",
            label: "Other options",
        },
        {
            label: "Bulk input modal",
            description: "If you want to use the bulk input modal for this check location, set this to the field you want to paste values for. Use in conjunction with `use_bulk_edit` in pre check form.",
            name: "bulk_input_modal",
            type: "single-select",
            options: ["pressure", "2_pressures", "weight"].map((i) => ({ value: i, label: i }))
        },
        {
            label: "Enable duplicate fruit button",
            description: "Enable button to duplicate latest fruit",
            name: "enable_duplicate_fruit",
            ...yesOrNoRadio
        },
        {
            label: "Require another check (deprecated)",
            description: "Once used to differentiate between first-ripening check and regular ripening checks. Not in use anymore.",
            name: "require_another_check",
            type: "single-select",
            options: config.check_locations.map(({ value, text }) => ({ value, label: `${text} (${value})` }))
        },
        {
            label: "Hide start check button",
            description: "Hide the button to start this check. For example if you want to start an intake check from the PO, you can hide it from the pallet here.",
            name: "is_hidden",
            ...yesOrNoRadio
        },
        {
            label: "Show fruit per box",
            description: "Divide the fruit per box by box_size. If you have box size 10 and 13 fruit, you will get 2 boxes. Setting this to false will always show one box.",
            name: "show_fruit_per_box",
            ...yesOrNoRadio
        },
        {
            label: "Only show latest check on child",
            description: "Set this to true for ART like checks to only show one calender item per ART.",
            name: "only_show_latest_check_on_child",
            ...yesOrNoRadio
        },
        {
            label: "Check card caption field",
            description: "Check field or check meta key to show under the check card on the layer page, e.g. order_label for closing checks. Leave empty to show nothing.",
            name: "card_caption_field",
            type: "text"
        },
        {
            label: "Show device weight",
            description: "Show device weight in device results",
            name: "show_atron_weight",
            ...yesOrNoRadio
        },
        {
            label: "Show device color",
            description: "Show device predicted color class",
            name: "show_atron_color",
            ...yesOrNoRadio
        },
        {
            label: "Show firmness results bottom summary",
            description: "Show medium and average firmness below device results",
            name: "show_atron_results_bottom_summary",
            ...yesOrNoRadio
        },

        {
            label: "Show scanner button on check page",
            description: "Show 'Next pallet' button on the check page. This will open the barcode scanner.",
            name: "show_scanner_button_on_check_page",
            ...yesOrNoRadio
        },
        {
            label: "Display in the barcode scanner shortcut",
            description: "Check will be shown as an option in barcode scanner shortcut.",
            name: "show_in_barcode_scanner_shortcut",
            ...yesOrNoRadio
        },
        {
            label: "Show advanced FTA check page",
            description: "Show advanced FTA check page.",
            name: "show_advanced_fta_check_page",
            ...yesOrNoRadio
        },

    ];

    return (
        <div className="d-flex flex-column gap-5">
            <SanitizeUnexpectedFields form={form} object={config_data} setObject={setConfigData} />

            <MetaForm
                groupFields
                withSearch
                meta={form}
                setValue={(field, value) => setConfigData({ ...config_data, [field]: value })}
                object={config_data}
                config={config}
            />
        </div>
    );
}
